import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { inject, Provider } from "@angular/core";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from "@azure/msal-angular";
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from "@azure/msal-browser";
import { environment } from "~environments/environment";
import { AppInsightsService } from "./common/services/app-insights.service";

function loggerCallback(logLevel: LogLevel, message: string) {
    try {
        const appInsightsService = inject(AppInsightsService);
        appInsightsService.onMsalException(logLevel, message);
    } catch {
        switch (logLevel) {
            case LogLevel.Error:
                console.error(message);
                break;
            case LogLevel.Warning:
                console.warn(message);
                break;
        }
    }
}

function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.msalConfig.auth.clientId,
            authority: environment.msalConfig.auth.authority,
            redirectUri: "/core",
            postLogoutRedirectUri: "/core/login",
            clientCapabilities: ["CP1"]
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Warning,
                piiLoggingEnabled: false
            }
        }
    });
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();
    protectedResourceMap.set(`${environment.apiProxy}/*`, environment.apiConfig.scopes);
    protectedResourceMap.set(`${environment.api}/*`, environment.apiConfig.scopes);
    protectedResourceMap.set(`${environment.apiV2}/*`, environment.apiConfig.scopes);
    protectedResourceMap.set(`${environment.mapProxy}`, environment.apiConfig.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: protectedResourceMap
    };
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.apiConfig.scopes]
        },
        loginFailedRoute: "/core/permissiondenied"
    };
}

export const msalProviders: Provider[] = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
    },
    {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
];
